import { useState, useContext, Fragment } from "react";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { fetchVisitantes } from "../services/trackingServiceV2";
import { toast } from "react-toastify";
import DateFilterBar from "../components/DateFilterBar";
import JornadaModal, { UtmBadges } from "../components/JornadaModal";

// Visitantes por uid canônico (unificação é física no banco — nada de agrupar por IP),
// só nas páginas do produto selecionado, paginado no servidor.

const PAGE_SIZE = 50;

function formatR$(v) {
    return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

export default function Visitantes() {
    const { isOpen } = useContext(SidebarContext);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [jornadaVisitor, setJornadaVisitor] = useState(null);
    const [ultimo, setUltimo] = useState(null); // { startDate, endDate, filters, filtroCompra }

    async function carregar(params, page) {
        setLoading(true);
        try {
            const result = await fetchVisitantes(params.startDate, params.endDate, params.filtroCompra, params.filters, page, PAGE_SIZE);
            setData(result);
            setExpandedRow(null);
        } catch {
            toast.error("Erro ao buscar visitantes");
        }
        setLoading(false);
    }

    function handleBuscar(startDate, endDate, filters = {}, filtroCompra = "todos") {
        const params = { startDate, endDate, filters, filtroCompra };
        setUltimo(params);
        carregar(params, 1);
    }

    function irPara(page) {
        if (!ultimo || !data) return;
        if (page < 1 || page > data.total_pages) return;
        carregar(ultimo, page);
    }

    function copyResumo() {
        if (!data || !ultimo) return;
        const statusMap = { todos: "Todos", comprou: "Comprou", nao_comprou: "Não Comprou" };
        const linhas = ["📊 Resumo do Relatório — Visitantes", "", "🔎 Filtros aplicados:", `  Período: ${ultimo.startDate} a ${ultimo.endDate}`];
        if (ultimo.filters.produto_id) linhas.push(`  Produto: ${ultimo.filters.produto_id}`);
        for (const k of ["pagina", "campanha", "medium", "content", "term"]) if (ultimo.filters[k]) linhas.push(`  ${k[0].toUpperCase() + k.slice(1)}: ${ultimo.filters[k]}`);
        linhas.push(`  Status: ${statusMap[ultimo.filtroCompra] || ultimo.filtroCompra}`, "", "📈 Resultados:",
            `  Total Visitantes: ${data.kpis.total}`, `  Compraram: ${data.kpis.compraram}`, `  Não Compraram: ${data.kpis.nao_compraram}`,
            `  Taxa de Conversão: ${data.kpis.taxa_conversao}%`, `  Valor Total: ${formatR$(data.kpis.valor_total)}`);
        navigator.clipboard.writeText(linhas.join("\n")).then(() => toast.success("Resumo copiado!")).catch(() => toast.error("Erro ao copiar"));
    }

    function extractPath(url) {
        try { const u = new URL(url); return u.hostname + u.pathname; } catch { return url; }
    }

    const Chip = ({ n, cls }) => Number(n) > 0 ? <span className={`${cls} px-2 py-0.5 rounded-full text-xs`}>{n}</span> : <span className="text-ink-5 text-xs">—</span>;

    return (
        <div className="min-h-screen bg-surface-1 text-white flex">
            <main className={`transition-all duration-300 ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full p-6 mb-16 sm:mb-0`}>
                <header className="mb-6">
                    <h1 className="text-3xl font-bold mb-1">Visitantes</h1>
                    <p className="text-sm text-ink-3 mb-4">Uma linha por pessoa (uid unificado), nas páginas do produto selecionado.</p>
                    <DateFilterBar onBuscar={handleBuscar} loading={loading} showPurchaseFilter />
                </header>

                {!data && !loading && <p className="text-center text-ink-4 py-8">Selecione um período e clique em Buscar</p>}
                {loading && <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" /></div>}

                {data && !loading && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 flex-1">
                                {[
                                    { label: "Total Visitantes", value: data.kpis.total, color: "text-blue-400" },
                                    { label: "Compraram", value: data.kpis.compraram, color: "text-emerald-400" },
                                    { label: "Não Compraram", value: data.kpis.nao_compraram, color: "text-red-400" },
                                    { label: "Taxa Conversão", value: data.kpis.taxa_conversao + "%", color: "text-yellow-400" },
                                    { label: "Valor Total", value: formatR$(data.kpis.valor_total), color: "text-emerald-300" },
                                ].map(kpi => (
                                    <div key={kpi.label} className="bg-surface-2 p-4 rounded-lg">
                                        <p className="text-xs text-ink-3">{kpi.label}</p>
                                        <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                                    </div>
                                ))}
                            </div>
                            <button onClick={copyResumo} title="Copiar resumo do relatório"
                                className="bg-surface-2 hover:bg-surface-3 text-ink-2 hover:text-white p-4 rounded-lg transition-colors flex flex-col items-center gap-1 whitespace-nowrap">
                                <span className="text-lg">📋</span><span className="text-xs">Copiar</span>
                            </button>
                        </div>

                        <div className="bg-surface-2 rounded-lg overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/[0.08] text-ink-3">
                                        <th className="text-left p-3">Visitante</th>
                                        <th className="text-left p-3">Páginas</th>
                                        <th className="text-left p-3">Lead</th>
                                        <th className="text-left p-3">Clicou</th>
                                        <th className="text-left p-3">Chegou</th>
                                        <th className="text-left p-3">Vídeo</th>
                                        <th className="text-left p-3">Status</th>
                                        <th className="text-left p-3">Valor</th>
                                        <th className="text-left p-3">Primeiro Acesso</th>
                                        <th className="text-left p-3">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.visitantes && data.visitantes.length > 0 ? data.visitantes.map((v, i) => (
                                        <Fragment key={v.visitor_id}>
                                            <tr className="border-b border-white/[0.06] hover:bg-surface-3/30">
                                                <td className="p-3 cursor-pointer" onClick={() => setExpandedRow(expandedRow === i ? null : i)}>
                                                    <p className="font-medium text-sm">{v.nome || v.email || v.visitor_id.substring(0, 18) + "..."}</p>
                                                    <UtmBadges utms={{ utm_source: v.utm_source, utm_medium: v.utm_medium, utm_campaign: v.utm_campaign, utm_term: v.utm_term, utm_content: v.utm_content }} />
                                                    {v.produtos_nomes?.length > 1 && <p className="text-[10px] text-ink-4 mt-1">{v.produtos_nomes.join(" · ")}</p>}
                                                </td>
                                                <td className="p-3"><Chip n={v.total_pageviews} cls="bg-blue-900/40 text-blue-400 font-medium" /></td>
                                                <td className="p-3"><Chip n={v.total_leads} cls="bg-cyan-900/40 text-cyan-400" /></td>
                                                <td className="p-3"><Chip n={v.total_intents} cls="bg-yellow-900/40 text-yellow-400" /></td>
                                                <td className="p-3"><Chip n={v.total_checkouts} cls="bg-orange-900/40 text-orange-400" /></td>
                                                <td className="p-3"><Chip n={v.total_videos} cls="bg-indigo-900/40 text-indigo-400" /></td>
                                                <td className="p-3">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v.comprou ? "bg-emerald-900/50 text-emerald-400" : "bg-surface-3 text-ink-3"}`}>
                                                        {v.comprou ? `✅ Comprou${Number(v.qtd_compras) > 1 ? ` ×${v.qtd_compras}` : ""}` : "Visitante"}
                                                    </span>
                                                </td>
                                                <td className="p-3 text-emerald-400 font-medium whitespace-nowrap">{Number(v.valor_compra) > 0 ? formatR$(v.valor_compra) : "—"}</td>
                                                <td className="p-3 text-ink-3 text-xs whitespace-nowrap">{v.primeiro_acesso ? new Date(v.primeiro_acesso).toLocaleString("pt-BR") : "—"}</td>
                                                <td className="p-3">
                                                    <button onClick={() => setJornadaVisitor(v.visitor_id)} className="text-emerald-400 hover:text-emerald-300 text-xs underline whitespace-nowrap">Ver Jornada</button>
                                                </td>
                                            </tr>
                                            {expandedRow === i && (
                                                <tr className="bg-surface-3/20">
                                                    <td colSpan={10} className="px-6 py-2 text-xs text-ink-3">
                                                        <span className="text-ink-4">Páginas: </span>{(v.urls || []).map(extractPath).join(" · ") || "—"}
                                                        <span className="text-ink-4 ml-3">Último acesso: </span>{v.ultimo_acesso ? new Date(v.ultimo_acesso).toLocaleString("pt-BR") : "—"}
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    )) : (
                                        <tr><td colSpan={10} className="text-center text-ink-4 p-6">Nenhum visitante encontrado</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {data.total_pages > 1 && (
                            <div className="flex items-center justify-between text-xs text-ink-3">
                                <span>Página {data.page} de {data.total_pages} · {data.kpis.total} visitantes</span>
                                <div className="flex gap-2">
                                    <button onClick={() => irPara(data.page - 1)} disabled={data.page <= 1} className="px-3 py-1.5 rounded bg-surface-3 hover:bg-surface-4 disabled:opacity-40">← Anterior</button>
                                    <button onClick={() => irPara(data.page + 1)} disabled={data.page >= data.total_pages} className="px-3 py-1.5 rounded bg-surface-3 hover:bg-surface-4 disabled:opacity-40">Próxima →</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {jornadaVisitor && <JornadaModal visitorId={jornadaVisitor} onClose={() => setJornadaVisitor(null)} />}
        </div>
    );
}
